import { useTranslation } from 'react-i18next';
import { EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import AlertBox from '@/components/alert-box';
import { cn } from '@/libs/utils';

/**
 * Right-hand details panel of a profile page: title, Edit (or the edit badge
 * while editing), the
 * unsaved-changes notice, and the selected tile's content.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {boolean} props.isEditing
 * @param {() => void} [props.onEdit] - when the open tile can be edited
 * @param {string[]} [props.dirtyLabels] - names of tiles with unsaved changes
 * @param {React.ReactNode} [props.before] - shown above the form
 * @param {React.ReactNode} [props.after] - shown below the form
 * @param {string} [props.className]
 */
export default function ProfileDetailsPanel({
  title,
  isEditing,
  onEdit,
  dirtyLabels = [],
  before,
  after,
  className,
  children,
}) {
  const { t } = useTranslation(['forms', 'messages']);

  return (
    <aside
      aria-label={title}
      data-tour="profile-panel"
      className={cn(
        'flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[14px] border border-zinc-200 bg-white',
        className
      )}
    >
      <div className="flex items-end justify-between gap-2.5 border-b border-zinc-200 px-3.5 py-3">
        <h2 className="m-0 text-base font-extrabold">{title}</h2>
        {!isEditing && onEdit && (
          <Button
            size="small"
            data-tour="profile-edit"
            icon={<EditOutlined />}
            className="btn-default !rounded-[50px]"
            onClick={onEdit}
          >
            {t('edit', { ns: 'forms' })}
          </Button>
        )}
        {isEditing && (
          <span className="rounded-full bg-[rgba(var(--accentColor),0.1)] px-2.5 py-0.5 text-xs font-semibold text-accent">
            {t('edit', { ns: 'forms' })}
          </span>
        )}
      </div>
      {isEditing && dirtyLabels.length > 0 && (
        <AlertBox
          variant="warning"
          className="!m-0 !rounded-none border-x-0 border-t-0 py-2 text-[13px]"
        >
          {t('pleaseSaveYourChanges', { ns: 'messages' })}{' '}
          <span className="font-normal">({dirtyLabels.join(', ')})</span>
        </AlertBox>
      )}
      <div className="employee-profile__panel min-h-0 flex-1 overflow-auto">
        {before && <div className="px-3.5 pt-3">{before}</div>}
        {children}
        {after && <div className="px-3.5 pb-3.5">{after}</div>}
      </div>
    </aside>
  );
}
